import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getCurrentLanguage, t } from "../i18n";

const WATCHLIST_NAME_SUGGESTIONS = [
  "Second Watchlist",
  "Third Watchlist",
  "Fourth Watchlist",
  "Fifth Watchlist",
];

function normalizeWatchlists(payload) {
  const apiData = payload?.data || payload || {};
  const statusText = String(apiData?.status || payload?.status || "").toLowerCase();
  const rawWatchlists = Array.isArray(apiData?.watchlists)
    ? apiData.watchlists
    : Array.isArray(payload?.watchlists)
      ? payload.watchlists
      : [];
  const watchlists =
    rawWatchlists.length === 1 &&
    rawWatchlists[0] &&
    typeof rawWatchlists[0] === "object" &&
    !Array.isArray(rawWatchlists[0]) &&
    !("id" in rawWatchlists[0]) &&
    !("watchlist_id" in rawWatchlists[0]) &&
    Object.keys(rawWatchlists[0]).every((k) => /^\d+$/.test(k))
      ? Object.values(rawWatchlists[0])
      : rawWatchlists;

  if (statusText !== "success") return [];
  return watchlists
    .map((item) => ({
      id: String(item?.id || item?.watchlist_id || "").trim(),
      name: String(item?.watchlist_name || item?.name || item?.watchlistName || "").trim(),
    }))
    .filter((item) => item.id && item.name);
}

export default function SelectWatchlistModal({
  isOpen,
  onClose,
  selectedStock,
  pmsApiBaseUrl,
  sharedData,
}) {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const tr = useCallback((key) => t(key, currentLanguage), [currentLanguage]);

  const [view, setView] = useState("select");
  const [isLoading, setIsLoading] = useState(false);
  const [watchlists, setWatchlists] = useState([]);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [watchlistError, setWatchlistError] = useState("");
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);

  const brokerFlag = useMemo(
    () =>
      String(
        localStorage.getItem("broker_type") || sharedData?.broker_type || "5paisa",
      ).toLowerCase(),
    [sharedData],
  );
  const userId = useMemo(
    () => Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0,
    [sharedData],
  );

  const watchlistNames = useMemo(
    () => watchlists.map((w) => w.name).filter(Boolean),
    [watchlists],
  );

  useEffect(() => {
    const onLang = (e) => setCurrentLanguage(e?.detail?.language || getCurrentLanguage());
    window.addEventListener("app-language-change", onLang);
    return () => window.removeEventListener("app-language-change", onLang);
  }, []);

  const fetchWatchlists = useCallback(async () => {
    if (!userId || !brokerFlag) {
      setWatchlists([]);
      return [];
    }
    try {
      const response = await fetch(`${pmsApiBaseUrl}/api/v1/watchlist/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, broker_flag: brokerFlag }),
      });
      const data = await response.json();
      const list = normalizeWatchlists(data);
      setWatchlists(list);
      return list;
    } catch (_) {
      setWatchlists([]);
      return [];
    }
  }, [brokerFlag, pmsApiBaseUrl, userId]);

  useEffect(() => {
    if (!isOpen) {
      setView("select");
      setNewWatchlistName("");
      setWatchlistError("");
      setIsSubmittingCreate(false);
      return;
    }
    let alive = true;
    (async () => {
      setIsLoading(true);
      setWatchlists([]);
      try {
        await fetchWatchlists();
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [brokerFlag, fetchWatchlists, isOpen, pmsApiBaseUrl, userId]);

  const addStockToWatchlist = useCallback(
    async (watchlist) => {
      if (!selectedStock) {
        toast.error(tr("noStockSelected"));
        return false;
      }
      const watchlistId = String(watchlist?.id || "").trim();
      const scriptCode = String(
        selectedStock?.ScripCode ??
          selectedStock?.Scripcode ??
          selectedStock?.scripcode ??
          "",
      ).trim();
      const stockName = String(
        selectedStock?.FullName ||
          selectedStock?.Name ||
          selectedStock?.name ||
          selectedStock?.Symbol ||
          "",
      ).trim();
      const tradingSymbol = String(
        selectedStock?.TradingSymbol ||
          selectedStock?.Symbol ||
          selectedStock?.name ||
          selectedStock?.Name ||
          "",
      ).trim();
      const rawExchange = String(selectedStock?.Exch || "").toUpperCase();
      const exchangeFor5Paisa = rawExchange || "N";
      const exchangeForGrow =
        rawExchange === "B" ? "BSE" : rawExchange === "M" ? "MCX" : "NSE";

      if (!watchlistId || !userId || !brokerFlag) {
        toast.error(tr("failedToAddToWatchlist"));
        return false;
      }

      let requestData;
      if (brokerFlag === "grow") {
        if (!tradingSymbol) {
          toast.error(tr("failedToAddToWatchlist"));
          return false;
        }
        requestData = {
          action: "add",
          watchlist_id: watchlistId,
          user_id: userId,
          broker_flag: brokerFlag,
          trading_symbol: tradingSymbol,
          exchange: exchangeForGrow,
        };
      } else {
        if (!scriptCode || !stockName) {
          toast.error(tr("failedToAddToWatchlist"));
          return false;
        }
        requestData = {
          action: "add",
          watchlist_id: watchlistId,
          user_id: userId,
          broker_flag: brokerFlag,
          script_code: scriptCode,
          name: stockName,
          exchange: exchangeFor5Paisa,
        };
      }

      try {
        const response = await fetch(`${pmsApiBaseUrl}/api/v1/watchlist/symbol`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });
        const data = await response.json();
        const apiData = data?.data || data || {};
        const status = String(apiData?.status || "").toLowerCase();
        const message = apiData?.message || tr("failedToAddToWatchlist");

        if (status === "error") {
          toast.error(message);
          return false;
        }
        if (status === "success") {
          toast.success(message);
          return true;
        }
        toast.error(message);
        return false;
      } catch (_) {
        toast.error(tr("failedToAddToWatchlist"));
        return false;
      }
    },
    [brokerFlag, pmsApiBaseUrl, selectedStock, tr, userId],
  );

  const handlePickWatchlist = async (watchlist) => {
    const ok = await addStockToWatchlist(watchlist);
    if (ok) onClose?.();
  };

  const handleCancelCreate = () => {
    setNewWatchlistName("");
    setWatchlistError("");
    setView("select");
  };

  const handleBackdropClick = () => {
    if (view === "create") {
      handleCancelCreate();
      return;
    }
    onClose?.();
  };

  const handleCreateWatchlistAndAdd = async () => {
    setWatchlistError("");
    const trimmedName = newWatchlistName.trim();

    if (!trimmedName) {
      setWatchlistError(tr("watchlistNameCannotBeEmpty"));
      return;
    }
    if (trimmedName.length > 30) {
      setWatchlistError(tr("watchlistNameUnder30"));
      return;
    }
    if (watchlistNames.some((n) => n.toLowerCase() === trimmedName.toLowerCase())) {
      setWatchlistError(tr("watchlistNameAlreadyExists"));
      return;
    }
    if (watchlists.length >= 5) {
      setWatchlistError(tr("maximum5WatchlistsAllowed"));
      return;
    }
    if (!userId || !brokerFlag) {
      setWatchlistError(tr("failedToCreateWatchlist"));
      return;
    }

    setIsSubmittingCreate(true);
    try {
      const response = await fetch(`${pmsApiBaseUrl}/api/v1/watchlist/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          broker_flag: brokerFlag,
          watchlist_name: trimmedName,
        }),
      });
      const data = await response.json();
      const apiData = data?.data || {};
      const status = String(apiData?.status || "").toLowerCase();
      const message = apiData?.message || tr("failedToCreateWatchlist");

      if (status !== "success") {
        setWatchlistError(message);
        return;
      }

      toast.success(message);
      setNewWatchlistName("");
      setWatchlistError("");

      const latest = await fetchWatchlists();
      const created = latest.find(
        (w) => w.name.toLowerCase() === trimmedName.toLowerCase(),
      );
      if (created) {
        const ok = await addStockToWatchlist(created);
        if (ok) onClose?.();
      } else {
        toast.error(tr("failedToAddToWatchlist"));
      }
    } catch (_) {
      setWatchlistError(tr("failedToCreateWatchlist"));
    } finally {
      setIsSubmittingCreate(false);
    }
  };

  if (!isOpen) return null;

  const stockTitle =
    selectedStock?.Name ||
    selectedStock?.FullName ||
    selectedStock?.Symbol ||
    "";
  const stockInitial = (stockTitle || "S").charAt(0).toUpperCase();
  const exchLabel =
    selectedStock?.Exch === "N"
      ? "NSE"
      : selectedStock?.Exch === "B"
        ? "BSE"
        : selectedStock?.Exch === "M"
          ? "MCX"
          : selectedStock?.Exch || "—";
  const typeLabel =
    selectedStock?.ExchType === "D" || String(selectedStock?.ExchType || "").startsWith("D")
      ? "Derivative"
      : "Cash";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        zIndex: 200000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="selectWatchlistModalTitle"
        style={{
          width: "100%",
          maxWidth: "480px",
          border: "none",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        }}
      >
        {view === "select" && (
          <>
            <div
              className="modal-header"
              style={{
                border: "none",
                padding: "28px 32px 20px 32px",
                position: "relative",
                backgroundColor: "#ffffff",
              }}
            >
              <button
                type="button"
                className="btn-close"
                aria-label={tr("close")}
                onClick={() => onClose?.()}
                style={{
                  position: "absolute",
                  top: "24px",
                  right: "24px",
                  opacity: 0.5,
                  width: "24px",
                  height: "24px",
                }}
              />
              <div style={{ paddingRight: "40px" }}>
                <h2
                  id="selectWatchlistModalTitle"
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#0f172a",
                    letterSpacing: "-0.4px",
                    lineHeight: 1.25,
                  }}
                >
                  Add to Watchlist
                </h2>
              </div>
            </div>

            <div
              className="modal-body"
              style={{ padding: "0 32px 28px 32px", backgroundColor: "#ffffff" }}
            >
              {selectedStock && (
                <div
                  style={{
                    padding: "16px 18px",
                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    borderRadius: "12px",
                    marginBottom: "24px",
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "linear-gradient(135deg, #2457BF 0%, #1a42a0 100%)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "18px",
                      flexShrink: 0,
                      boxShadow: "0 4px 12px rgba(36, 87, 191, 0.25)",
                    }}
                  >
                    {stockInitial}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h5
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {stockTitle || "—"}
                    </h5>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        color: "#64748b",
                        fontWeight: 500,
                      }}
                    >
                      {exchLabel} • {typeLabel}
                    </p>
                  </div>
                </div>
              )}

              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  marginBottom: "16px",
                  fontWeight: 500,
                }}
              >
                Select a watchlist
              </p>

              {isLoading && (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderWidth: "3px",
                      color: "#2457BF",
                    }}
                  >
                    <span className="visually-hidden">{tr("loading")}</span>
                  </div>
                  <p style={{ marginTop: "16px", fontSize: "14px", color: "#64748b" }}>
                    Loading watchlists...
                  </p>
                </div>
              )}

              {!isLoading && watchlists.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 20px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "12px",
                    border: "1px dashed #cbd5e1",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 16px 0",
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: 500,
                    }}
                  >
                    No watchlists found
                  </p>
                  {watchlists.length < 5 && (
                    <button
                      type="button"
                      onClick={() => {
                        setWatchlistError("");
                        setView("create");
                      }}
                      style={{
                        padding: "10px 20px",
                        fontSize: "14px",
                        fontWeight: 600,
                        backgroundColor: "#2457BF",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      Create your first watchlist
                    </button>
                  )}
                </div>
              )}

              {!isLoading && watchlists.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    marginBottom: "20px",
                  }}
                >
                  {watchlists.map((watchlist, index) => (
                    <button
                      key={watchlist.id || index}
                      type="button"
                      onClick={() => handlePickWatchlist(watchlist)}
                      style={{
                        padding: "14px 16px",
                        fontSize: "15px",
                        backgroundColor: "#ffffff",
                        color: "#0f172a",
                        border: "2px solid #e2e8f0",
                        borderRadius: "10px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      <span>{watchlist.name}</span>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                        <path
                          d="M6 12L10 8L6 4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ))}
                </div>
              )}

              {!isLoading && watchlists.length > 0 && watchlists.length < 5 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "24px 0",
                    gap: "16px",
                  }}
                >
                  <div style={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }} />
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#94a3b8",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                    }}
                  >
                    OR
                  </span>
                  <div style={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }} />
                </div>
              )}

              {!isLoading && watchlists.length < 5 && (
                <button
                  type="button"
                  onClick={() => {
                    setWatchlistError("");
                    setView("create");
                  }}
                  style={{
                    width: "100%",
                    padding: "13px 20px",
                    fontSize: "15px",
                    fontWeight: 600,
                    backgroundColor: "#ffffff",
                    color: "#2457BF",
                    border: "2px solid #2457BF",
                    borderRadius: "10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M8 3V13M3 8H13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {tr("createNewWatchlist")}
                </button>
              )}

              {!isLoading && watchlists.length >= 5 && (
                <div
                  style={{
                    padding: "14px 16px",
                    background: "linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%)",
                    borderRadius: "10px",
                    marginTop: "16px",
                    fontSize: "14px",
                    color: "#92400e",
                    border: "1px solid #fde68a",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontWeight: 500,
                  }}
                >
                  <span>{tr("maximum5WatchlistsReached")}</span>
                </div>
              )}
            </div>
          </>
        )}

        {view === "create" && (
          <>
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #2457BF 0%, #1a42a0 100%)",
                border: "none",
                padding: "24px 28px",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                    <path
                      d="M9 3V15M3 9H15"
                      stroke="#ffffff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                >
                  {tr("createNewWatchlist")}
                </h2>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancelCreate}
                aria-label={tr("close")}
                style={{
                  filter: "brightness(0) invert(1)",
                  opacity: 0.9,
                }}
              />
            </div>

            <div className="modal-body" style={{ padding: "28px", backgroundColor: "#F2F7FD" }}>
              {watchlists.length >= 5 && (
                <div
                  style={{
                    padding: "16px 18px",
                    background: "linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%)",
                    borderRadius: "12px",
                    marginBottom: "24px",
                    border: "1px solid rgba(231, 76, 60, 0.3)",
                  }}
                >
                  <p style={{ margin: 0, color: "#c62828", fontSize: "14px" }}>
                    {tr("maximum5WatchlistsAllowedDetailed")}
                  </p>
                </div>
              )}

              <div style={{ marginBottom: "24px" }}>
                <label
                  htmlFor="swlNewWatchlistName"
                  style={{
                    display: "block",
                    marginBottom: "10px",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#1a1a1a",
                  }}
                >
                  {tr("watchlistName")}{" "}
                  <span style={{ color: "#D67A1F" }}>*</span>
                </label>
                <input
                  type="text"
                  id="swlNewWatchlistName"
                  placeholder={tr("watchlistNamePlaceholder")}
                  value={newWatchlistName}
                  maxLength={30}
                  disabled={watchlists.length >= 5 || isSubmittingCreate}
                  onChange={(e) => {
                    setNewWatchlistName(e.target.value);
                    setWatchlistError("");
                  }}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "14px 16px",
                    fontSize: "14px",
                    border: watchlistError ? "2px solid #ef5350" : "2px solid #e0e7ef",
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    color: "#1a1a1a",
                    outline: "none",
                    fontWeight: 500,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "8px",
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  <span style={{ color: newWatchlistName.length >= 25 ? "#D67A1F" : "#64748b" }}>
                    {newWatchlistName.length}/30 {tr("characters")}
                  </span>
                  {newWatchlistName.length >= 25 && (
                    <span style={{ color: "#D67A1F", fontSize: "11px" }}>
                      {30 - newWatchlistName.length} {tr("remaining")}
                    </span>
                  )}
                </div>
                {watchlistError && (
                  <div
                    style={{
                      color: "#c62828",
                      fontSize: "13px",
                      marginTop: "10px",
                      padding: "10px 12px",
                      backgroundColor: "#fff5f5",
                      borderRadius: "8px",
                      border: "1px solid rgba(231, 76, 60, 0.2)",
                    }}
                  >
                    {watchlistError}
                  </div>
                )}
              </div>

              {watchlists.length < 5 && (
                <div style={{ marginBottom: "24px" }}>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#64748b",
                      marginBottom: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {tr("quickSuggestions")}
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {WATCHLIST_NAME_SUGGESTIONS.filter((s) => !watchlistNames.includes(s))
                      .slice(0, 5 - watchlists.length)
                      .map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => {
                            setNewWatchlistName(suggestion);
                            setWatchlistError("");
                          }}
                          style={{
                            padding: "8px 16px",
                            fontSize: "13px",
                            fontWeight: 500,
                            backgroundColor: "#ffffff",
                            color: "#2457BF",
                            border: "1.5px solid #e0e7ef",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                        >
                          {suggestion}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <div
                style={{
                  fontSize: "13px",
                  color: "#334155",
                  padding: "16px 18px",
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e0e7ef",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: watchlistNames.length > 0 ? "12px" : 0,
                  }}
                >
                  <strong style={{ fontSize: "14px", color: "#1a1a1a" }}>
                    {tr("yourWatchlists")}
                  </strong>
                  <span
                    style={{
                      padding: "4px 12px",
                      backgroundColor: watchlists.length >= 5 ? "#fff5f5" : "#f0f9ff",
                      color: watchlists.length >= 5 ? "#c62828" : "#2457BF",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {watchlists.length}/5
                  </span>
                </div>
                {watchlistNames.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {watchlistNames.map((name) => (
                      <span
                        key={name}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "6px 12px",
                          backgroundColor: "#F2F7FD",
                          color: "#334155",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: 500,
                          border: "1px solid #e0e7ef",
                        }}
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                borderTop: "1px solid #e0e7ef",
                padding: "20px 28px",
                backgroundColor: "#ffffff",
                display: "flex",
                gap: "12px",
              }}
            >
              <button
                type="button"
                onClick={handleCancelCreate}
                disabled={isSubmittingCreate}
                style={{
                  padding: "11px 24px",
                  fontSize: "14px",
                  fontWeight: 600,
                  backgroundColor: "#f1f5f9",
                  color: "#475569",
                  border: "none",
                  borderRadius: "10px",
                  cursor: isSubmittingCreate ? "not-allowed" : "pointer",
                }}
              >
                {tr("cancel")}
              </button>
              <button
                type="button"
                onClick={handleCreateWatchlistAndAdd}
                disabled={
                  watchlists.length >= 5 || !newWatchlistName.trim() || isSubmittingCreate
                }
                style={{
                  flex: 1,
                  padding: "11px 24px",
                  fontSize: "14px",
                  fontWeight: 600,
                  background:
                    watchlists.length >= 5 || !newWatchlistName.trim() || isSubmittingCreate
                      ? "#cbd5e1"
                      : "linear-gradient(135deg, #2457BF 0%, #1a42a0 100%)",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "10px",
                  cursor:
                    watchlists.length >= 5 || !newWatchlistName.trim() || isSubmittingCreate
                      ? "not-allowed"
                      : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {isSubmittingCreate ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path
                      d="M7 2V12M2 7H12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {tr("createWatchlist")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
